import { useEffect, useRef, useState } from 'react';

import { cn } from '@utils/cn';

import { Icon } from '@ui-component-library/components/Icon';
import { Text } from '@ui-component-library/components/Text';
import { Tooltip } from '@ui-component-library/components/Tooltip';

import {
  dropdownListClasses,
  fluidLabelClasses,
  iconWrapperClasses,
  listItemClasses,
  selectContainerClasses,
  selectedValueClasses,
  selectInputClasses,
  topSectionClasses,
  wrapperClasses
} from './Dropdown.styles';
import type { DropdownProps, ValidationState } from './Dropdown.types';

export const Dropdown = ({
  name = 'example_dropdown',
  label,
  description,
  options = [],
  variant = 'default',
  disabled = false,
  value,
  placeholder,
  required = false,
  onChange = () => {},
  onFocus = () => {},
  isValid,
  tabIndex = 0,
  className
}: DropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [, setIsFocused] = useState(false);
  const [validationState, setValidationState] = useState<ValidationState>('idle');

  const wrapperRef = useRef<HTMLDivElement>(null);

  const isFluid = variant === 'fluid';
  const isDisabled = disabled;

  const normalizedOptions = options.map((option) => ({
    ...option,
    value: option.value
  }));

  const selectedOption = normalizedOptions.find((option) => option.value === value);
  const displayText = selectedOption ? selectedOption.label : placeholder;

  /* eslint-disable react-hooks/set-state-in-effect -- Intentional: sync validation state with prop */
  useEffect(() => {
    if (isValid === undefined) {
      return;
    }
    setValidationState(isValid ? 'success' : 'error');
  }, [isValid]);
  /* eslint-enable react-hooks/set-state-in-effect */

  // CRITICAL: Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      // Closes the list if the click target is not within the component's wrapper
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setIsFocused(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Toggle dropdown
  const toggleList = () => {
    if (isDisabled) {
      return;
    }
    setIsOpen((prev) => !prev);
    setIsFocused(true);
    onFocus();
  };

  const handleSelect = (selectedValue: string) => {
    const valid = selectedValue !== '';
    setValidationState(valid ? 'success' : 'error');
    setIsOpen(false);
    setIsFocused(false);

    onChange(selectedValue, valid, name);
  };

  return (
    <div ref={wrapperRef} className={cn(wrapperClasses({ isDisabled }), className)}>
      {variant !== 'fluid' && (
        <div className={topSectionClasses()}>
          <Text variant="text-sm/leading-normal/normal">
            {label}
            {required && <span className="text-red-500">*</span>}
          </Text>
          {!isDisabled && description && (
            <Text variant="text-sm/leading-normal/normal">{description}</Text>
          )}
        </div>
      )}

      <div className="relative">
        <div
          className={cn(
            selectContainerClasses({
              state: isDisabled ? 'disabled' : validationState
            })
          )}
        >
          {isFluid && (
            <Text variant="text-xs/leading-normal/normal" className={fluidLabelClasses()}>
              <Tooltip
                aria-label="tooltip"
                content={description}
              >
                <span className="flex items-center">
                  {label}
                  {required && <span className="ml-1 text-red-500">*</span>}
                  {description && (
                    <span className="pl-1">
                      <Icon name="InfoFilled" size="small" color="#6b7280" />
                    </span>
                  )}
                </span>
              </Tooltip>
            </Text>
          )}

          <div
            tabIndex={tabIndex}
            aria-expanded={isOpen}
            aria-controls={`${name}-list`}
            role="button"
            onClick={toggleList}
            className={selectInputClasses({ isFluid })}
          >
            <Text
              className={cn(selectedValueClasses({ hasValue: !!value }))}
              variant="text-sm/leading-normal/normal"
            >
              {displayText}
            </Text>
          </div>

          <input type="hidden" name={name} value={value ?? ''} />

          <div className={cn(iconWrapperClasses())}>
            <Icon name="ChevronDownSimple" />
          </div>
        </div>

        {isOpen && (
          <ul
            id={`${name}-list`}
            role="listbox"
            onMouseDown={(e) => {
              e.stopPropagation();
            }}
            className={cn(dropdownListClasses(), className)}
          >
            {normalizedOptions.map((opt) => (
              <li
                key={opt.value}
                role="option"
                aria-selected={opt.value === String(value)}
                className={listItemClasses({ isSelected: opt.value === String(value) })}
                onClick={() => {
                  handleSelect(opt.value);
                }}
              >
                {opt.label}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};
